//************************************************************************ /
// DETAIL VIEW
// Displays the details for an event or a fundraiser on the community board.
// Arrived at when a user clicks an event or fundraiser card on the community board.
//************************************************************************ /

import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { GetStaticPaths, GetStaticProps } from "next";
import { fetcher } from "@dreambigger/shared/src/api/api";
import {
  generateCloudinaryUrl,
  getCoverImage,
} from "@dreambigger/shared/src/utils";
import { eventPaths } from "@dreambigger/shared/src/api/events";
import { useTryAsync } from "no-try";
import { organizationPaths } from "@dreambigger/shared/src/api/organizations";
import { communityPaths } from "@dreambigger/shared/src/api/communities";
import { useRouter } from "next/router";
import cotributeDefaults from "@dreambigger/shared/src/assets/cotribute-defaults";
import useUser from "@dreambigger/shared/src/hooks/use-user";
import {
  Event as EventType,
  Community,
  OrgBootstrap,
  User,
} from "@dreambigger/shared/src/types";
import {
  Divider,
  Head,
  message,
  Spin,
  Typography,
  Group,
  Banner,
  AppcuesButton,
} from "@dreambigger/design-system/src/components/";
import {
  api,
  tokenHelper,
  communityApi,
  orgApi,
  eventApi,
} from "@dreambigger/shared/src/api/weserve/api-helper";
import { EventBreadcrumbs, ShareStoryCtaCard, UnsubscribeModal } from "../ui";
import { EventContent, FundraiserContent } from "../content";
import StoryBoard from "@dreambigger/shared/src/components/board/stories-board";
import InspireAnalytics, {
  InspireAnalyticEvents,
  InspireAnalyticsHelper,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import {
  useMixpanel,
  useGA,
  useAppcues,
  useSegment,
} from "@dreambigger/shared/src/hooks";
import { generateWeserveQueryParams } from "@dreambigger/shared/src/utils/query-params";
import { QuestionOutlined } from "@ant-design/icons";

export type EventProps = {
  event: EventType;
  orgBootstrap: OrgBootstrap;
  community: Community;
};

const { Title } = Typography;
const STORY_PAGE_SIZE = 12;

// -----------------------------------------------------------------------------------------------------------------
//   ASYNC EXPORT STATIC GENERATION FUNCTIONS
//   * These functions are currently called in @dreambigger/cotribute-shell/events/[eventId]/detail.tsx
//     and allow us to pass in orgBootstrap, event, eventStories, and community as props to the Event component below.
// -----------------------------------------------------------------------------------------------------------------

// Get static paths
export const getEventStaticPaths: GetStaticPaths = async () => {
  const res = await api.get("/active-event-ids");
  const eventIds: number[] = res.data.eventIds;

  // Only statically generate the last X amount of events. Default to 300
  const staticPageLimit = process.env.ENTITY_STATIC_PAGE_LIMIT || 300;

  return {
    paths: eventIds
      .slice(0, Math.min(Number(staticPageLimit), eventIds.length))
      .map((id) => ({ params: { eventId: String(id) } })),
    fallback: true,
  };
};

// Get static props to be passed into Event component.
export const getEventStaticProps: GetStaticProps = async ({ params }) => {
  const eventId = params!.eventId as string;
  // *** Get event data.
  const [eventError, eventRes] = await useTryAsync(() =>
    eventApi.getById(Number(eventId))
  );
  if (eventError) {
    console.error("Unable to load event:", eventError);
    return {
      notFound: true,
    };
  }

  // Assign results to variables.
  const event: EventType = eventRes?.data;

  // Get orgBootstrap data.
  const orgBootstrapRes = await orgApi.getOrgBootstrap(event.organization.id);
  const orgBootstrap = orgBootstrapRes.data;
  // Return all constants as props.
  const returnProps = {
    event,
    orgBootstrap,
  } as EventProps;

  // Get community data
  const [communityError, communityResponse] = await useTryAsync(() =>
    communityApi.getBySlug(orgBootstrap?.slug)
  );
  // Add community data to returnProps
  if (!communityError && communityResponse?.data) {
    returnProps.community = communityResponse.data;
  }
  // Return the returnProps, revalidating every 120 seconds.
  return {
    props: returnProps,
    revalidate: 120,
  };
};

const Event = ({
  orgBootstrap: initialOrgBootstrap,
  event: initialEvent,
  community: initialCommunityData,
}: EventProps) => {
  const router = useRouter();

  // Load Analytics and Appcues
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();
  const appcues = useAppcues();

  // Store the currently logged-in user, if present.
  const [userLoading, { data: user }] = useUser(api, tokenHelper);

  // Fetch event metadata to compare against statically generated data
  const { isFallback } = useRouter();
  const fetcherFn = (url: string) => fetcher(api, tokenHelper, url);

  const { data: eventData, mutate: mutateEvent } = useSWR<EventType, any>(
    initialEvent?.id ? eventPaths.getById(initialEvent.id) : null,
    fetcherFn,
    {
      fallbackData: initialEvent,
      onError: (error) => {
        console.error(error);
        message.error("Something went wrong loading this event.");
      },
    }
  );

  // Handle activatefundraiser + product_id query params from email CTAs
  // Legacy behavior: update the event with changed_to_fundraiser, then reload event data.
  // The emailed CTA also carries a `hash` refresh token, which AuthProvider
  // exchanges for a session before rendering children — so by the time this
  // effect runs the request is authenticated.
  const activateFundraiserHandled = useRef(false);
  useEffect(() => {
    if (userLoading || !router.isReady || activateFundraiserHandled.current)
      return;
    const activatefundraiser = router.query.activatefundraiser as
      | string
      | undefined;
    const productId = router.query.product_id as string | undefined;
    if (!activatefundraiser || !productId || !initialEvent?.id) return;

    activateFundraiserHandled.current = true;
    const changedToFundraiser = activatefundraiser.toLowerCase() === "yes";

    const apiCall = changedToFundraiser
      ? eventApi.patchActivateFundraiser(initialEvent.id, productId)
      : eventApi.patchDeclineFundraiser(initialEvent.id, productId);

    apiCall
      .then(() => mutateEvent())
      .catch((err: any) =>
        console.error("Failed to update fundraiser status:", err)
      );
  }, [
    userLoading,
    router.isReady,
    router.query,
    initialEvent?.id,
    mutateEvent,
  ]);

  // Handle modal=unsubscribe query param from action engine emails.
  // Redirects to a server-side page that proxies the PUT to weserve, because
  // weserve's CORS policy does not allow PUT from the browser.
  const [unsubscribeModal, setUnsubscribeModal] = useState<{
    visible: boolean;
    success: boolean;
    eventTitle: string;
  }>({ visible: false, success: false, eventTitle: "" });
  const unsubscribeRedirected = useRef(false);
  useEffect(() => {
    if (!router.isReady || !initialEvent?.id) return;
    const modal = router.query.modal as string | undefined;
    if (modal !== "unsubscribe") return;

    // If returning from the server-side unsubscribe page, show the result modal
    const unsubscribeSuccess = router.query.unsubscribeSuccess as
      | string
      | undefined;
    if (unsubscribeSuccess !== undefined) {
      const eventTitle = (router.query.eventTitle as string) || "";
      setUnsubscribeModal({
        visible: true,
        success: unsubscribeSuccess === "true",
        eventTitle,
      });
      return;
    }

    // Redirect to server-side unsubscribe page (handles the PUT to weserve)
    if (unsubscribeRedirected.current) return;
    const eaid = router.query.eaid as string | undefined;
    if (!eaid) return;
    unsubscribeRedirected.current = true;
    const token = tokenHelper.getJwtToken();
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
    router.replace(`/unsubscribe/${initialEvent.id}/${eaid}${tokenParam}`);
  }, [router.isReady, router.query, initialEvent?.id]);

  const handleUnsubscribeClose = () => {
    setUnsubscribeModal((prev) => ({ ...prev, visible: false }));
    router.replace(`/events/${initialEvent?.id}/detail`, undefined, {
      shallow: true,
    });
  };

  // Track the page view for analytics and appcues.
  useEffect(() => {
    // Give GoogleAnalytics time to initialize
    // Wait for a response on whether a user is logged in.
    if (userLoading) {
      return;
    }
    googleAnalytics.setDefaultDimensions(community.slug, user);
    googleAnalytics.pageview(window.location.pathname);
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.PAGE_VIEW,
      properties: {
        Page: window.location.pathname,
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
        "Host Name": window.location.host,
      },
      mixpanel,
      segment,
    });
    // If there is a user, track user page visit on appcues
    if (!user?.id) {
      return;
    }
    appcues.identify(user.id.toString(), {
      firstName: user.firstName,
      lastName: user.lastName,
      created_at: new Date(),
      communitySlug: community.slug,
      orgName: orgBootstrap.name,
      projectTitle: event.name,
      projectVisibility: event.visibilityStatus,
      projectType: !!event.isFundraiser,
      eventId: event.id,
      groupId: event.id,
      imageId: user.imageId,
    });
  }, [userLoading]);

  // Fetch orgBootstrap metadata to compare against statically generated data
  const { data: orgBootstrapData } = useSWR<OrgBootstrap, any>(
    initialEvent?.organization.id
      ? organizationPaths.getOrgBootstrapPath(initialEvent.organization.id)
      : null,
    fetcherFn,
    {
      fallbackData: initialOrgBootstrap,
    }
  );
  // Fetch community metadata to compare against statically generated data
  const { data: communityData } = useSWR<Community, any>(
    initialEvent?.organization.community?.slug
      ? communityPaths.getBySlug(initialEvent.organization.community.slug)
      : null,
    fetcherFn,
    {
      fallbackData: initialCommunityData,
    }
  );

  // Assign metadata variables using fetched data, if available, or default to using the static data.
  const orgBootstrap = orgBootstrapData || initialOrgBootstrap;
  const event = eventData || initialEvent;
  const community = communityData || initialCommunityData;
  const hasImages = (event?.images && event?.images.length > 0) || undefined;
  const coverImage =
    (hasImages && getCoverImage(event.images)) || cotributeDefaults.coverImage;

  // Success Banner Logic - Displays a success banner under various url conditions
  const [messageApi, contextHolder] = message.useMessage();
  const bannerShownRef = useRef(false);
  // Drive the inline RSVP banner from local state so we can strip
  // ?rsvp=success from the URL on first render without unmounting the banner.
  // rsvpBannerShownRef matches the bannerShownRef pattern used above: ensures
  // the banner only fires once per page session, so a dismissed banner cannot
  // pop back if the effect re-runs (e.g. when initialEvent?.id resolves later
  // in the Next.js fallback rendering lifecycle).
  const rsvpBannerShownRef = useRef(false);
  const [rsvpBanner, setRsvpBanner] =
    useState<{
      recipients?: string;
    } | null>(null);

  useEffect(() => {
    if (userLoading) return; // Wait until contextHolder is mounted
    if (bannerShownRef.current) return;
    const hasSuccess =
      router.query.edit === "success" || router.query.donation === "success";
    if (hasSuccess) bannerShownRef.current = true;

    router.query.edit === "success" && displayEditSuccessBanner();
    router.query.donation === "success" && displayDonationSuccessBanner();
  }, [router, userLoading]);

  useEffect(() => {
    if (rsvpBannerShownRef.current) return;
    if (router.query.rsvp !== "success" || !initialEvent?.id) return;
    rsvpBannerShownRef.current = true;
    setRsvpBanner({
      recipients: router.query.recipients as string | undefined,
    });
    router.replace(`/events/${initialEvent.id}/detail`, undefined, {
      shallow: true,
    });
  }, [router.query.rsvp, router.query.recipients, initialEvent?.id]);

  const displayEditSuccessBanner = () => {
    messageApi.open({
      type: "success",
      duration: 3,
      icon: <></>,
      content: (
        <Banner
          icon="checkmark"
          text1="We have successfully updated this project"
          textColor="white"
          darkHoverText={true}
          iconBackground="#36905b"
          uiColor="white"
          style={{
            backgroundColor: "#359960",
            borderTop: "1px solid #359960",
            borderLeft: "1px solid #359960",
            borderRight: "1px solid #359960",
            borderBottom: "2px solid #359960",
          }}
        />
      ),
    });
  };

  const displayDonationSuccessBanner = () => {
    messageApi.open({
      type: "success",
      duration: 5,
      icon: <></>,
      content: (
        <Banner
          icon="checkmark"
          text1="Thank you! Your donation has been submitted."
          textColor="white"
          darkHoverText={true}
          iconBackground="#359960"
          uiColor="white"
          style={{
            backgroundColor: "#359960",
            borderTop: "1px solid #359960",
            borderLeft: "1px solid #359960",
            borderRight: "1px solid #359960",
            borderBottom: "2px solid #359960",
          }}
        />
      ),
    });
    // Remove the query param so banner doesn't show again on refresh
    router.replace(`/events/${event.id}/detail`, undefined, { shallow: true });
  };

  // (Currently Unused) Store event images. May eventually need all images for a gallery feature.
  const images =
    (hasImages &&
      event?.images.map((img) =>
        generateCloudinaryUrl(img.imageVersion, img.imageId)
      )) ||
    [];

  // Prepare for final render. Check if page has been statically rendered. If not (such as when using yarn run dev),
  // display loading spinner while data is fetched.
  if (isFallback || userLoading) {
    return <Spin style={{ width: "100%", marginTop: 150 }} />;
  }

  // Guard against scenario where no data is found.
  if (!event) {
    message.error("Error loading page");
    return null;
  }

  const headMeta = {
    favicon: community?.meta?.brand?.tabIcon || cotributeDefaults.tabIconIco,
    title: event.name || "Event",
    description: "Check out how much fun we had making a difference together!",
    siteName: "Events",
    image: coverImage,
  };

  const sendAnotherInvite = () => {
    router.push(`/actions/invites/event/${event.id}`);
  };

  const sendAnotherThankYouNote = () => {
    router.push(`/events/${event.id}/thankyou`);
  };

  const tynRecipientCount = router.query.tynrc;

  // **************************************************************
  // * RENDER THE VIEW
  // **************************************************************
  return (
    <>
      <Head {...headMeta} />
      <UnsubscribeModal
        visible={unsubscribeModal.visible}
        success={unsubscribeModal.success}
        eventTitle={unsubscribeModal.eventTitle}
        onClose={handleUnsubscribeClose}
      />
      <div className="default-wrapper-padding pv-6">
        {contextHolder}

        {/* Load a success banner if redirected from a RSVP flow */}
        {rsvpBanner && (
          <Banner
            icon="checkmark"
            className="mb-5"
            text1={
              rsvpBanner.recipients
                ? `Your invitation has been sent to ${rsvpBanner.recipients} ${
                    rsvpBanner.recipients === "1" ? "recipient" : "recipients"
                  }!`
                : `Your invitation has been sent!`
            }
            textColor="white"
            closable={true}
            darkHoverText={true}
            iconBackground="#359960"
            ctaText="SEND ANOTHER"
            ctaLinkOrFunction={sendAnotherInvite}
            uiColor="white"
            onClose={() => setRsvpBanner(null)}
            style={{
              backgroundColor: "#359960",
              borderTop: "1px solid #D9D9D9",
              borderLeft: "1px solid #D9D9D9",
              borderRight: "1px solid #D9D9D9",
              borderBottom: "2px solid #D9D9D9",
            }}
          />
        )}

        {/* Load a success banner if redirected from a Thank You flow */}
        {router.query.tynrc && (
          <>
            <Banner
              icon="checkmark"
              className="mb-5"
              text1={
                tynRecipientCount
                  ? `Your Thank You Note has been sent to ${tynRecipientCount} ${
                      tynRecipientCount === "1" ? "recipient" : "recipients"
                    }.`
                  : `Your Thank You Note has been sent.`
              }
              textColor="white"
              closable={true}
              darkHoverText={true}
              iconBackground="#359960"
              ctaText="SEND ANOTHER"
              ctaLinkOrFunction={sendAnotherThankYouNote}
              uiColor="white"
              style={{
                backgroundColor: "#359960",
                borderTop: "1px solid #D9D9D9",
                borderLeft: "1px solid #D9D9D9",
                borderRight: "1px solid #D9D9D9",
                borderBottom: "2px solid #D9D9D9",
              }}
            />
          </>
        )}

        {/* Display a closable banner for admins */}
        {event.isAdmin && (
          <>
            <Banner
              icon="earth-americas"
              className="mb-3 md_mb-5"
              text1={`Welcome to your project${
                user?.firstName ? `, ${user.firstName}!` : `!`
              }`}
              uiColor="#4a6097"
              text2={`This is how friends and family will view your page.`}
              closable={true}
              facebookShareEnabled={true}
              style={{
                backgroundColor: "#f9f9fd",
                backgroundImage:
                  "radial-gradient(#e9eff7 20%, transparent 40%)",
                backgroundSize: "7px 7px",
                backgroundRepeat: "repeat",
                borderTop: "1px solid #D9D9D9",
                borderLeft: "1px solid #D9D9D9",
                borderRight: "1px solid #D9D9D9",
                borderBottom: "2px solid #D9D9D9",
              }}
            />
          </>
        )}
        <EventBreadcrumbs event={event} community={community} />
        <div className="mb-8">
          {event.isFundraiser ? (
            <FundraiserContent
              community={community}
              coverImage={coverImage}
              event={event}
              orgBootstrap={orgBootstrap}
              user={user}
            />
          ) : (
            <EventContent
              community={community}
              coverImage={coverImage}
              event={event}
              user={user}
            />
          )}
          {/* Display additional stories if there are any. Cta card is different depending on if there are
              stories or not. */}
        </div>
        {event.storyCount > 0 && ( // When 0 stories, load <ShareStoryFooter> instead. See below.
          <Group spacing={4}>
            <Title level={4}>Latest Event Stories</Title>
            <Divider />
            <StoryBoard
              fixedFirstCard={<ShareStoryCtaCard event={event} />}
              boardTitle={null}
              community={community}
              getUrlKey={(pageIndex) =>
                eventPaths.getEventStoriesById(
                  event.id,
                  generateWeserveQueryParams({
                    offset: String(pageIndex * STORY_PAGE_SIZE),
                    limit: String(STORY_PAGE_SIZE),
                    sort: "created_at",
                    direction: "desc",
                  })
                )
              }
              showStoryComments={
                community?.organization?.meta?.showInspireComments || false
              }
              disableAnalytics={true} // Because we're using this as a subcomponent on the page and not a primary view, ensure that it isn't being tracked as a page view.
            />
          </Group>
        )}
        {/* FOOTER - When no stories are present for an event, a <ShareStoryFooter> is loaded.
            See this logic in _app.tsx. */}
      </div>
      {/* Thrivent Action Teams Only - Display a fixed "Help" button at the bottom right of the screen that launches an appcues flow */}
      {event.isAdmin && community.slug === "thrivent-action-teams" && (
        <>
          {/* Desktop Version */}
          <AppcuesButton
            appcuesFlow="-LbU9Szr_p96-PSLgC8k"
            additionalClassName="dn md_flex"
          />
          {/* Mobile Version */}
          <AppcuesButton
            appcuesFlow="-LbZUmHvdMyK20Qvq5_k"
            additionalClassName="md_dn"
            size="56px"
            icon={<QuestionOutlined style={{ fontSize: "20px" }} />}
            bottom="5px"
            right="7px"
          />
        </>
      )}
    </>
  );
};

export default Event;
